/**
 * PageSupplier — V2 Supplier Detail Page
 *
 * Replaces the V1 Supplier detail page with V2 design system components.
 * Tabs: Details, Documents, Users
 */

// react
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';

// context
import { ContextProject } from '../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2/ProjectV2.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { alpha, Box, Chip, Link, Stack, Tab, Tabs, Typography } from '@mui/material';

// icons
import {
	CloudUpload as IconUploadDocuments,
	PersonAdd as IconAddUser,
	DoNotDisturb as IconDeactivate,
	Edit as IconEdit,
	ModeEditOutlined as IconModeEdit,
	PlayArrow as IconActivate,
	Store as IconSupplier,
} from '@mui/icons-material';

// services
import { fetchAiDocuments } from '../../../services/aiApi';

// ui
import { BtnSplit } from '../../ui/BtnSplit/BtnSplit';

// v2 components
import { ColumnDef, DataTable, RowAction } from '../../ui/DataTable/DataTable';
import { HeaderDetailPage } from '../../ui/HeaderDetailPage/HeaderDetailPage';
import { customFieldsToStats } from '../../ui/CustomFieldsFormSection/CustomFieldsFormSection';

// dialogs
import { DialogAddUser } from '../../dialogs/DialogAddUser/DialogAddUser';
import { DialogDocumentViewer } from '../../dialogs/DialogDocumentViewer/DialogDocumentViewer';
import { DialogEditSupplier } from '../../dialogs/DialogEditSupplier/DialogEditSupplier';
import { DialogEditUser } from '../../dialogs/DialogEditUser/DialogEditUser';
import { DialogUpdateEntityStatus } from '../../dialogs/DialogUpdateEntityStatus/DialogUpdateEntityStatus';
import { DialogUpdateUserStatus } from '../../dialogs/DialogUpdateUserStatus/DialogUpdateUserStatus';
import { DialogUploadDocument } from '../../dialogs/DialogUploadDocument/DialogUploadDocument';

// types
import { Entity, Loan, Pagination, User } from '../../../@types';
import type {
	AiDocument,
	AiDocumentUser,
} from '../ProjectV2/Documents/documentTypes';
import { formatDate } from '../../../utils/dates';

// Helpers

/**
 * Extract a contract id from an AiDocument. Prefers the API-populated
 * contract_id field (joined from metadata.contract_id), and falls back to
 * a `contract:<id>` entry in metadata.tags.
 */
const resolveContractIdFromDoc = (doc: AiDocument): string | null => {
	if (doc.contract_id) return String(doc.contract_id);
	const tags = (doc.metadata?.tags as unknown) as string[] | undefined;
	if (Array.isArray(tags)) {
		for (const t of tags) {
			const m = /^contract:(\d+)$/i.exec(t);
			if (m) return m[1];
		}
	}
	return null;
};

/** Infer DialogDocumentViewer file type from filename extension. */
const inferViewerFileType = (filename: string): 'pdf' | 'doc' | 'image' | 'xlsx' => {
	const ext = (filename.split('.').pop() || '').toLowerCase();
	if (ext === 'pdf') return 'pdf';
	if (['xls', 'xlsx', 'csv'].includes(ext)) return 'xlsx';
	if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff'].includes(ext)) return 'image';
	return 'doc';
};

/** Format a byte count as a human-readable size for the viewer sidebar. */
const formatBytes = (bytes: number | null | undefined): string => {
	if (!bytes || bytes <= 0) return '—';
	const units = ['B', 'KB', 'MB', 'GB'];
	const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
	return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
};

/** Map an AiDocument to the minimal ContractDocument shape DialogDocumentViewer expects. */
const aiDocToContractDoc = (doc: AiDocument) => ({
	id: doc.id,
	name: doc.filename,
	category: 'other' as const,
	type: inferViewerFileType(doc.filename),
	size: formatBytes(((doc.metadata?.file_size_bytes as number) || 0) as number),
	uploadedAt: doc.uploaded_at,
	uploadedBy:
		doc.uploaded_by && typeof doc.uploaded_by === 'object'
			? (doc.uploaded_by as AiDocumentUser).displayName ||
				(doc.uploaded_by as AiDocumentUser).email ||
				'—'
			: '—',
});

/** Map an AiDocument to ViewerDocumentInfo (rich sidebar metadata). */
const aiDocToViewerInfo = (doc: AiDocument) => ({
	id: doc.id,
	name: doc.filename,
	tags: (doc.metadata?.tags as string[]) || [],
	sourceType: (doc.metadata?.source_type as string) || undefined,
	size: formatBytes(((doc.metadata?.file_size_bytes as number) || 0) as number),
	uploadedAt: doc.uploaded_at,
	uploadedBy:
		doc.uploaded_by && typeof doc.uploaded_by === 'object'
			? (doc.uploaded_by as AiDocumentUser).displayName ||
				(doc.uploaded_by as AiDocumentUser).email ||
				undefined
			: undefined,
	jobId: doc.job_id || undefined,
});

const getEntityStatusConfig = (status: string, t: (k: string) => string) => {
	switch (status) {
		case 'ACTIVE':
			return { color: '#16a34a', label: t('active') };
		case 'INACTIVE':
			return { color: '#dc2626', label: t('inactive') };
		case 'PENDING':
			return { color: '#d97706', label: t('pending') };
		default:
			return { color: '#6b7280', label: status || t('active') };
	}
};

// Component

export const PageSupplierV2 = () => {
	const { t } = useTranslation();
	const { supplierId } = useParams<{ supplierId: string }>();
	const [searchParams, setSearchParams] = useSearchParams();

	// context
	const { project } = useContext(ContextProject);
	const { contracts } = useContext(ContextSpendPlan);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// tab state — synced to ?tab=users|documents so browser back/forward navigates tabs
	const tabParam = searchParams.get('tab');
	const activeTab: 'users' | 'documents' = tabParam === 'documents' ? 'documents' : 'users';
	const setActiveTab = (value: 'users' | 'documents') => {
		setSearchParams(
			(params) => {
				if (value === 'users') {
					params.delete('tab');
				} else {
					params.set('tab', value);
				}
				return params;
			},
			{ replace: false }
		);
	};

	// entity state
	const [supplier, setSupplier] = useState<Entity | null>(null);
	const [loans, setLoans] = useState<Loan[]>([]);
	const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
	const [showUploadDocumentsDialog, setShowUploadDocumentsDialog] = useState(false);

	// user/doc action state
	const [editUser, setEditUser] = useState<User | null>(null);
	const [deactivateUser, setDeactivateUser] = useState<User | null>(null);

	// documents state
	const [documents, setDocuments] = useState<AiDocument[]>([]);
	const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);

	// document viewer (URL-driven via ?doc_id)
	const viewerDocId = searchParams.get('doc_id');
	const viewerDocIndex = useMemo(
		() => (viewerDocId ? documents.findIndex((d) => d.id === viewerDocId) : -1),
		[viewerDocId, documents]
	);
	const [paginationDocuments, setPaginationDocuments] = useState<Pagination>({
		page: 1,
		perPage: 10,
		total: 0,
		totalPages: 0,
	});
	const [searchDocuments, setSearchDocuments] = useState('');

	// users state
	const [users, setUsers] = useState<User[]>([]);
	const [paginationUsers, setPaginationUsers] = useState<Pagination>({
		page: 1,
		perPage: 10,
		total: 0,
		totalPages: 0,
	});
	const [searchUsers, setSearchUsers] = useState('');

	const openAddUserDialog = () => {
		setSearchParams((params) => {
			params.set('dialog', 'addUser');
			return params;
		});
	};

	const closeAddUserDialog = () => {
		setSearchParams((params) => {
			params.delete('dialog');
			return params;
		});
	};

	const headers = useMemo(
		() => ({
			Authorization: `Bearer ${accessToken}`,
			User: String(idToken),
		}),
		[accessToken, idToken]
	);

	// Fetch supplier
	const { isLoading: isLoadingSupplier, refetch: refetchSupplier } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/entities/${supplierId}?project=${project?.id}&populate=wallet`,
		options: { headers },
		onSuccess: (res) => {
			if (res.data) {
				setSupplier(res.data[0] || res.data);
			}
		},
	});

	// Fetch loans
	useFetch({
		url: `${process.env.REACT_APP_API_URL}/loans?entity=${supplierId}&populate=entities`,
		options: { headers },
		onSuccess: (res) => {
			if (res?.data) {
				setLoans(res.data);
			}
		},
	});

	// Fetch documents from bldt-ai-fn, scoped by tag `supplier:<id>`
	const refetchDocuments = useCallback(async () => {
		if (!project?.id || !supplierId) return;
		setIsLoadingDocuments(true);
		try {
			const res = await fetchAiDocuments({
				project_id: project.id,
				tags: `supplier:${supplierId}`,
				search: searchDocuments || undefined,
				page: paginationDocuments.page,
				page_size: paginationDocuments.perPage,
				populate: 'uploaded_by',
				order_by: 'uploaded_at',
				order: 'DESC',
			});
			setDocuments(res.data || []);
			setPaginationDocuments((prev) => ({
				...prev,
				total: res.pagination?.total_count ?? 0,
				totalPages: res.pagination?.total_pages ?? 0,
			}));
		} catch {
			setDocuments([]);
		} finally {
			setIsLoadingDocuments(false);
		}
	}, [project?.id, supplierId, searchDocuments, paginationDocuments.page, paginationDocuments.perPage]);

	useEffect(() => {
		refetchDocuments();
	}, [refetchDocuments]);

	// Fetch users
	const { isLoading: isLoadingUsers, refetch: refetchUsers } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/users?entities=${supplierId}&populate=entities,country&page=${paginationUsers.page}&limit=${paginationUsers.perPage}${searchUsers ? `&q=${searchUsers}` : ''}`,
		options: { headers },
		onSuccess: (res) => {
			if (res?.data) {
				setUsers(res.data);
			}
			if (res?.pagination) {
				setPaginationUsers((prev) => ({
					...prev,
					total: res.pagination.total,
					totalPages: res.pagination.totalPages,
				}));
			}
		},
	});

	// Custom field definitions
	const customFieldDefs = useMemo(
		() =>
			(project?.customFieldDefinitions || []).find((d) => d.entityType === 'supplier')
				?.definitions || [],
		[project]
	);

	// Custom field stats
	const customFieldStats = useMemo(
		() => customFieldsToStats(customFieldDefs, supplier?.customData || {}),
		[customFieldDefs, supplier]
	);

	// Contracts linked to this supplier
	const supplierContracts = useMemo(() => {
		if (!supplier) return [];
		return contracts.filter((c) => {
			return c.entities?.some((e) => e.type === 'SUPPLIER' && e.id === supplier.id);
		});
	}, [contracts, supplier]);

	// Associated parent entities from loans
	const associatedEntities = useMemo(() => {
		const entities: { id: number; name: string }[] = [];
		loans.forEach((loan) => {
			const loanEntities = (loan as { entities?: { id?: number; name?: string; type?: string }[] })
				.entities;
			if (Array.isArray(loanEntities)) {
				loanEntities.forEach((e) => {
					if (e && e.id && e.type !== 'SUPPLIER' && !entities.find((x) => x.id === e.id)) {
						entities.push({ id: e.id, name: e.name || `Entity #${e.id}` });
					}
				});
			}
		});
		return entities;
	}, [loans]);

	// Build header stats
	const walletStatus = supplier?.wallet?.status || 'ACTIVE';
	const statusConfig = getEntityStatusConfig(walletStatus, t);

	const headerStats = supplier
		? [
				{
					label: t('status'),
					value: (
						<Chip
							label={statusConfig.label}
							size="small"
							sx={{
								bgcolor: alpha(statusConfig.color, 0.1),
								color: statusConfig.color,
								fontSize: '1rem',
								fontWeight: 600,
								height: 28,
								px: 1,
							}}
						/>
					),
				},
				...(supplier.parent && typeof supplier.parent !== 'number'
					? [{ label: t('parentEntity'), value: supplier.parent.name }]
					: []),
				...(supplier.idExternal ? [{ label: t('externalId'), value: supplier.idExternal }] : []),
				{
					label: t('contracts'),
					value: `${supplierContracts.length}`,
				},
				...(supplier.wallet?.address
					? [
							{
								label: t('wallet'),
								value: `${supplier.wallet.address.slice(0, 10)}…`,
							},
						]
					: []),
				{
					label: t('kycVerified'),
					value: supplier.isKycVerified ? 'Yes' : 'No',
				},
				...(associatedEntities.length > 0
					? [{ label: t('associatedEntities'), value: `${associatedEntities.length}` }]
					: []),
				...(loans.length > 0 ? [{ label: t('financingSources'), value: `${loans.length}` }] : []),
				{
					label: t('created'),
					value: formatDate({ date: supplier.created }),
				},
				{
					label: t('lastUpdated'),
					value: formatDate({ date: supplier.modified }),
				},
				// Custom fields as inline stats
				...customFieldStats,
			]
		: [];

	// User row actions
	const userRowActions: RowAction<User>[] = useMemo(
		() => [
			{
				label: t('supplier.editUser'),
				icon: <IconModeEdit fontSize="small" />,
				onClick: (user: User) => setEditUser(user),
			},
			{
				label: t('supplier.deactivateUser'),
				icon: <IconDeactivate fontSize="small" />,
				onClick: (user: User) => {
					setSearchParams((params) => {
						params.set('dialogStatus', 'DEACTIVATED');
						return params;
					});
					setDeactivateUser(user);
				},
				destructive: true,
				dividerBefore: true,
				show: (user: User) => user.status !== 'DEACTIVATED',
			},
			{
				label: t('supplier.activateUser'),
				icon: <IconActivate fontSize="small" />,
				onClick: (user: User) => {
					setSearchParams((params) => {
						params.set('dialogStatus', 'INVITED');
						return params;
					});
					setDeactivateUser(user);
				},
				dividerBefore: true,
				show: (user: User) => user.status === 'DEACTIVATED',
			},
		],
		[setSearchParams]
	);

	// Document columns (AiDocument from bldt-ai-fn get_documents)
	const documentColumns: ColumnDef<AiDocument>[] = useMemo(
		() => [
			{
				id: 'filename',
				label: t('name'),
				minWidth: 200,
				render: (doc: AiDocument) => (
					<Stack>
						<Typography variant="body2" fontWeight={600}>
							{doc.filename}
						</Typography>
						{doc.doc_type && (
							<Typography variant="caption" color="text.secondary">
								{doc.doc_type}
							</Typography>
						)}
					</Stack>
				),
			},
			{
				id: 'doc_type',
				label: t('type'),
				minWidth: 120,
				render: (doc: AiDocument) => doc.doc_type || '—',
			},
			{
				id: 'contract',
				label: t('contract'),
				minWidth: 140,
				render: (doc: AiDocument) => {
					const contractId = resolveContractIdFromDoc(doc);
					if (!contractId || !project?.idWbg) return '—';
					const label = doc.contract_name || `#${contractId}`;
					return (
						<Link
							component={RouterLink}
							to={`/project/${project.idWbg}/spend-plans/contract/${contractId}`}
							underline="hover"
							sx={{ fontWeight: 600 }}
							onClick={(e) => e.stopPropagation()}>
							{label}
						</Link>
					);
				},
			},
			{
				id: 'uploaded_by',
				label: 'Uploaded By',
				minWidth: 140,
				render: (doc: AiDocument) => {
					const u = doc.uploaded_by;
					if (u && typeof u === 'object') {
						const user = u as AiDocumentUser;
						return user.displayName || user.email || '—';
					}
					return '—';
				},
			},
			{
				id: 'uploaded_at',
				label: t('uploaded'),
				minWidth: 120,
				sortable: true,
				render: (doc: AiDocument) => formatDate({ date: doc.uploaded_at }),
			},
		],
		[project?.idWbg]
	);

	// User columns
	const userColumns: ColumnDef<User>[] = useMemo(
		() => [
			{
				id: 'name',
				label: t('name'),
				minWidth: 180,
				render: (user: User) => (
					<Stack>
						<Typography variant="body2" fontWeight={600}>
							{user.fullName || `${user.firstName} ${user.lastName}`}
						</Typography>
						{user.title && (
							<Typography variant="caption" color="text.secondary">
								{user.title}
							</Typography>
						)}
					</Stack>
				),
			},
			{
				id: 'email',
				label: t('email'),
				minWidth: 200,
				render: (user: User) => user.email || '—',
			},
			{
				id: 'role',
				label: t('role'),
				minWidth: 120,
				render: (user: User) => user.role || '—',
			},
			{
				id: 'status',
				label: t('status'),
				minWidth: 100,
				render: (user: User) => {
					const isActive = user.status === 'ACTIVE';
					return (
						<Chip
							label={user.status || 'Active'}
							size="small"
							sx={{
								bgcolor: alpha(isActive ? '#16a34a' : '#6b7280', 0.1),
								color: isActive ? '#16a34a' : '#6b7280',
								fontWeight: 600,
							}}
						/>
					);
				},
			},
			{
				id: 'modified',
				label: t('lastUpdated'),
				minWidth: 120,
				sortable: true,
				render: (user: User) =>
					formatDate({ date: user.modified }),
			},
		],
		[]
	);

	// Not found
	if (!isLoadingSupplier && !supplier) {
		return (
			<Box sx={{ px: 3, py: 4 }}>
				<Typography>{t('supplier.notFound')}</Typography>
			</Box>
		);
	}

	return (
		<Box>
			{/* Header */}
			<HeaderDetailPage
				loading={isLoadingSupplier}
				icon={IconSupplier}
				breadcrumb={{
					label: t('suppliers'),
					to: `/project/${project?.idWbg}/spend-plans/suppliers`,
				}}
				title={supplier?.name || ''}
				variant="contract"
				stats={headerStats}
				customAction={
					supplier ? (
						<BtnSplit
							actions={[
								{
									children: t('edit'),
									startIcon: <IconEdit />,
									onClick: () => {
										setSearchParams((params) => {
											params.set('dialog', 'editSupplier');
											params.set('dialogSupplier', String(supplier.id));
											return params;
										});
									},
								},
								...(activeTab === 'users'
									? [
											{
												children: t('addUser'),
												startIcon: <IconAddUser />,
												onClick: openAddUserDialog,
											},
										]
									: []),
								...(activeTab === 'documents'
									? [
											{
												children: t('uploadDocuments'),
												startIcon: <IconUploadDocuments />,
												onClick: () => setShowUploadDocumentsDialog(true),
											},
										]
									: []),
								{
									children: supplier.wallet?.status === 'INACTIVE' ? t('activate') : t('deactivate'),
									startIcon:
										supplier.wallet?.status === 'INACTIVE' ? <IconActivate /> : <IconDeactivate />,
									onClick: () => setShowDeactivateDialog(true),
								},
							]}
						/>
					) : undefined
				}
			/>

			{/* Tabs */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					borderBottom: '1px solid',
					borderColor: 'divider',
					mb: 1,
				}}>
				<Tabs
					value={activeTab}
					onChange={(_, value) => setActiveTab(value)}
					variant="standard"
					sx={{ flex: 1, minHeight: 40 }}>
					<Tab
						label={t('users')}
						value="users"
						sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40, py: 0.5 }}
					/>
					<Tab
						label={t('documents')}
						value="documents"
						sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40, py: 0.5 }}
					/>
				</Tabs>
			</Box>

			{/* Tab Content */}
			<Box sx={{ px: 3, pb: 3, pt: 2 }}>
				{/* ============== Documents Tab ============== */}
				{activeTab === 'documents' && (
					<DataTable<AiDocument>
						data={documents}
						columns={documentColumns}
						getRowKey={(doc) => doc.id}
						loading={isLoadingDocuments}
						onRowClick={(doc) => {
							setSearchParams(
								(params) => {
									params.set('doc_id', doc.id);
									return params;
								},
								{ replace: false }
							);
						}}
						searchValue={searchDocuments}
						onSearchChange={setSearchDocuments}
						searchPlaceholder={t('supplier.searchDocuments')}
						page={paginationDocuments.page}
						rowsPerPage={paginationDocuments.perPage}
						totalCount={paginationDocuments.total || 0}
						onPageChange={(p) => setPaginationDocuments((prev) => ({ ...prev, page: p }))}
						onRowsPerPageChange={(rpp) =>
							setPaginationDocuments((prev) => ({
								...prev,
								page: 1,
								perPage: rpp,
							}))
						}
						emptyMessage={t('supplier.noDocuments')}
						emptyContent={t('supplier.noDocumentsContent')}
						emptyActions={[
							{
								children: t('uploadDocuments'),
								onClick: () => setShowUploadDocumentsDialog(true),
								variant: 'contained',
							},
						]}
					/>
				)}

				{/* ============== Users Tab ============== */}
				{activeTab === 'users' && (
					<DataTable<User>
						data={users}
						columns={userColumns}
						getRowKey={(user) => user.id}
						loading={isLoadingUsers}
						rowActions={userRowActions}
						searchValue={searchUsers}
						onSearchChange={setSearchUsers}
						searchPlaceholder={t('supplier.searchUsers')}
						page={paginationUsers.page}
						rowsPerPage={paginationUsers.perPage}
						totalCount={paginationUsers.total || 0}
						onPageChange={(p) => setPaginationUsers((prev) => ({ ...prev, page: p }))}
						onRowsPerPageChange={(rpp) =>
							setPaginationUsers((prev) => ({ ...prev, page: 1, perPage: rpp }))
						}
						emptyMessage={t('supplier.noUsers')}
						emptyContent={t('supplier.noUsersContent')}
						emptyActions={[
							{
								children: t('addUser'),
								onClick: openAddUserDialog,
								variant: 'contained',
							},
						]}
					/>
				)}
			</Box>

			{/* Edit Dialog */}
			<DialogEditSupplier
				isOpen={searchParams.get('dialog') === 'editSupplier'}
				onClose={() => {
					setSearchParams((params) => {
						params.delete('dialog');
						params.delete('dialogSupplier');
						return params;
					});
				}}
				onSuccess={() => {
					refetchSupplier();
				}}
			/>
			{supplier && showDeactivateDialog && (
				<DialogUpdateEntityStatus
					entity={supplier}
					isOpen={showDeactivateDialog}
					onClose={() => setShowDeactivateDialog(false)}
					onSuccess={() => {
						setShowDeactivateDialog(false);
						refetchSupplier();
					}}
				/>
			)}

			{supplier && (
				<DialogAddUser
					entity={supplier}
					isOpen={searchParams.get('dialog') === 'addUser'}
					onClose={closeAddUserDialog}
					onSuccess={() => {
						closeAddUserDialog();
						refetchUsers();
					}}
				/>
			)}

			{/* Edit User Dialog */}
			{editUser && (
				<DialogEditUser
					frame="ENTITY"
					user={editUser}
					isOpen={!!editUser}
					onClose={() => setEditUser(null)}
					onSuccess={() => {
						setEditUser(null);
						refetchUsers();
					}}
				/>
			)}

			{/* Deactivate/Activate User Dialog */}
			{deactivateUser && (
				<DialogUpdateUserStatus
					frame="ENTITY"
					user={deactivateUser}
					isOpen={!!deactivateUser}
					onClose={() => {
						setDeactivateUser(null);
						setSearchParams((params) => {
							params.delete('dialogStatus');
							return params;
						});
					}}
					onSuccess={() => {
						setDeactivateUser(null);
						setSearchParams((params) => {
							params.delete('dialogStatus');
							return params;
						});
						refetchUsers();
					}}
				/>
			)}

			{/* Upload Documents Dialog (bldt-ai-fn) */}
			{supplier && project && (
				<DialogUploadDocument
					isOpen={showUploadDocumentsDialog}
					onClose={() => setShowUploadDocumentsDialog(false)}
					projectId={project.id}
					sourceType="supplier"
					sourceId={supplier.id}
					onSuccess={() => {
						setShowUploadDocumentsDialog(false);
						refetchDocuments();
					}}
				/>
			)}

			{/* Document Viewer Dialog (bldt-ai-fn) */}
			{viewerDocIndex >= 0 && (
				<DialogDocumentViewer
					isOpen={!!viewerDocId}
					currentIndex={viewerDocIndex}
					documents={documents.map(aiDocToContractDoc)}
					blobFilenames={documents.map((d) => d.filename)}
					documentInfos={documents.map(aiDocToViewerInfo)}
					onClose={() => {
						setSearchParams(
							(params) => {
								params.delete('doc_id');
								return params;
							},
							{ replace: false }
						);
					}}
					onNavigate={(index) => {
						const next = documents[index];
						if (!next) return;
						setSearchParams(
							(params) => {
								params.set('doc_id', next.id);
								return params;
							},
							{ replace: true }
						);
					}}
				/>
			)}
		</Box>
	);
};
